<template>
  <div>
    <div
      class="trigger-wrapper"
      @click="recording ? stopRecord() : startRecord()"
    >
      <slot name="trigger">
        <van-button
          type="info"
          round
          style="width: 130px"
          color="#aa341b"
        >
          <van-icon
            :name="recording ? 'stop-circle' : 'volume-o'"
            style="margin-right: 6px"
          />{{ recording ? stopTriggerText : startTriggerText }}
        </van-button>
      </slot>
    </div>

    <!-- Full Screen Recording Overlay -->
    <van-popup
      v-model="recording"
      position="bottom"
      :style="{ height: '100%', width: '100%', zIndex: 10000 }"
      class="recording-popup"
      :overlay="false"
      get-container="body"
    >
      <div class="recording-container">
        <div class="close-btn-wrapper" @click="cancelRecord">
          <van-icon name="cross" size="24" color="#666" />
        </div>

        <div class="header-section">
          <div class="status-title">{{ displayTitle }}</div>
          <div class="status-desc">{{ displaySubTitle }}</div>
        </div>

        <div class="wave-section">
          <div class="wave-container">
            <div class="wave-bar bar-1"></div>
            <div class="wave-bar bar-2"></div>
            <div class="wave-bar bar-3"></div>
            <div class="wave-bar bar-4"></div>
            <div class="wave-bar bar-5"></div>
            <div class="wave-bar bar-6"></div>
            <div class="wave-bar bar-7"></div>
            <div class="wave-bar bar-8"></div>
          </div>
        </div>

        <div class="timer-section">
          <div class="time-display" :class="{ 'warning-text': remainingTime <= 10 }">
            {{ formattedTime }}
          </div>
          <div class="time-limit-hint">{{ timeLimitHint }}</div>
        </div>

        <div class="action-section">
          <div class="stop-btn" @click="stopRecord">
            <div class="stop-icon-square"></div>
            <span>{{ stopButtonText }}</span>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Vue from 'vue';
import {wecomSDK} from '@/utils/wecom';
import {Popup, Icon, Button} from 'vant';

Vue.use(Button);
Vue.use(Icon);
Vue.use(Popup);

export default {
  name: 'SpeechToText',
  components: {
    [Popup.name]: Popup,
    [Icon.name]: Icon,
    [Button.name]: Button
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    subTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      recording: false,
      startTime: 0,
      elapsedSeconds: 0,
      timerInterval: null,
      maxDuration: 60, // WeCom limit 60s
    };
  },
  computed: {
    isEnglishLocale() {
      return this.$i18n && /^en/i.test(this.$i18n.locale);
    },
    displayTitle() {
      return this.title || (this.isEnglishLocale ? 'AI is listening' : 'AI 正在倾听');
    },
    displaySubTitle() {
      return this.subTitle || (this.isEnglishLocale ? 'Please say your question...' : '请说出您的问题...');
    },
    startTriggerText() {
      return this.isEnglishLocale ? 'Voice Input' : '语音输入';
    },
    stopTriggerText() {
      return this.isEnglishLocale ? 'Stop Recording' : '停止录音';
    },
    timeLimitHint() {
      return this.isEnglishLocale ? 'Up to 60 seconds' : '最多60秒';
    },
    stopButtonText() {
      return this.isEnglishLocale ? 'Finish Recording' : '结束录音';
    },
    formattedTime() {
      const mins = Math.floor(this.elapsedSeconds / 60);
      const secs = this.elapsedSeconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    progressPercentage() {
      return Math.min((this.elapsedSeconds / this.maxDuration) * 100, 100);
    },
    remainingTime() {
      return Math.max(this.maxDuration - this.elapsedSeconds, 0);
    }
  },
  destroyed() {
    this.clearTimer();
    if (this.recording) {
      this.stopRecord(false);
    }
  },
  mounted() {
    wecomSDK.register();
  },
  methods: {
    startTimer() {
      this.startTime = Date.now();
      this.elapsedSeconds = 0;
      this.clearTimer();
      this.timerInterval = setInterval(() => {
        const now = Date.now();
        this.elapsedSeconds = Math.round((now - this.startTime) / 1000);
      }, 1000);
    },
    clearTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    cancelRecord() {
      this.stopRecord(false);
    },
    async startRecord() {
      this.recording = true;
      this.startTimer();
      await wecomSDK.startRecord({
        onVoiceRecordEnd: (res) => {
          this.stopRecord(true, res ? res.localId : null);
        },
      });
    },
    async stopRecord(translate = true, autoStopLocalId = null) {
      if (!this.recording) return;

      try {
        this.recording = false;
        this.clearTimer();
        const duration = this.elapsedSeconds;

        let localId = autoStopLocalId;
        if (!localId) {
          const res = await wecomSDK.stopRecord();
          localId = res.localId;
        }

        if (!localId) {
          throw new Error('No localId returned');
        }

        if (!translate) return;

        const {translateResult} = await wecomSDK.translateVoice(localId);
        this.$emit('translateResult', translateResult);
        this.$emit('recordComplete', {localId, translateResult, duration});
      } catch (error) {
        // console.error('Stop record failed', error);
        this.recording = false;
        this.clearTimer();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.trigger-wrapper {
  display: flex;
  justify-content: center;
}

.close-btn-wrapper {
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 10px;
  z-index: 10;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
}

.recording-popup {
  background-color: #fff;
  display: flex;
  flex-direction: column;
}

.recording-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  background: white;
  padding-bottom: 80px;
}

.header-section {
  text-align: center;
  margin-bottom: 60px;

  .status-title {
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
  }

  .status-desc {
    font-size: 16px;
    color: #999;
  }

  .status-limit {
    font-size: 12px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-bottom: 40px;
  }
}

.wave-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 60px;
}

.wave-bar {
  width: 4px;
  border-radius: 4px;
  animation: wave 1.2s ease-in-out infinite;
  height: 10px;
}

.bar-1 { background: #ffcccc; animation-delay: -0.8s; height: 20px; }
.bar-2 { background: #ffaaaa; animation-delay: -0.6s; height: 35px; }
.bar-3 { background: #ff8888; animation-delay: -0.4s; height: 25px; }
.bar-4 { background: #ff7777; animation-delay: -0.2s; height: 45px; }
.bar-5 { background: #ff7777; animation-delay: -0.3s; height: 40px; }
.bar-6 { background: #ff8888; animation-delay: -0.5s; height: 30px; }
.bar-7 { background: #ffaaaa; animation-delay: -0.7s; height: 20px; }
.bar-8 { background: #ffcccc; animation-delay: -0.9s; height: 15px; }

@keyframes wave {
  0%, 100% {
    transform: scaleY(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scaleY(1.2);
    opacity: 1;
  }
}

.timer-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 60px;
}

.time-display {
  font-size: 32px;
  color: #333;
  margin-top: 10px;
  margin-bottom: 16px;
  font-weight: 500;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  letter-spacing: 2px;
}

.warning-text {
  color: #cc341b;
}

.time-limit-hint {
  font-size: 15px;
  color: #999;
  margin-top: 10px;
}

.action-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stop-btn {
  background: #aa341b;
  color: white;
  width: 180px;
  height: 54px;
  border-radius: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  box-shadow: 0 6px 16px rgba(170, 52, 27, 0.25);
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    transform: scale(0.96);
    box-shadow: 0 2px 8px rgba(170, 52, 27, 0.25);
  }

  .stop-icon-square {
    width: 14px;
    height: 14px;
    background: white;
    border-radius: 2px;
    margin-right: 12px;
  }
}

</style>
